import { useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Block } from "@/types";
import { createBlock, uploadBlock, updateBlock, deleteBlock, reorderBlocks } from "@/api/exercises";
import Icon, { type IconName } from "@/components/Icon";

/** Constructeur de consigne par blocs. L'enseignant agence librement des blocs
 *  texte / indice / image / fichier (ordre + quantité libres), au glisser-déposer.
 *  Composant contrôlé : `blocks` est la source de vérité du parent (permet
 *  l'aperçu élève en direct) ; chaque mutation appelle l'API puis `onChange`. */
export default function BlockEditor({
  stepId,
  blocks,
  onChange,
}: {
  stepId: number;
  blocks: Block[];
  onChange: (blocks: Block[]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addText = async (kind: "text" | "hint") => {
    const b = await createBlock(stepId, { kind, text: "" });
    onChange([...blocks, b]);
  };

  const addMedia = async (file: File) => {
    const b = await uploadBlock(stepId, file);
    onChange([...blocks, b]);
  };

  // Met à jour le contenu localement (aperçu réactif) ; persiste en différé (blur).
  const patchLocal = (id: number, patch: Partial<Block>) =>
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const persist = (id: number, data: { text?: string; caption?: string; alt?: string }) =>
    updateBlock(id, data);

  const remove = async (id: number) => {
    await deleteBlock(id);
    onChange(blocks.filter((b) => b.id !== id));
  };

  const onDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    const next = arrayMove(blocks, oldIdx, newIdx);
    onChange(next);
    await reorderBlocks(stepId, next.map((b) => b.id));
  };

  return (
    <div style={styles.wrap}>
      {blocks.length === 0 && (
        <p style={styles.empty}>Consigne vide. Ajoute un bloc ci-dessous.</p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div style={styles.list}>
            {blocks.map((b) => (
              <SortableBlock
                key={b.id}
                block={b}
                onPatchLocal={patchLocal}
                onPersist={persist}
                onRemove={remove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div style={styles.addBar}>
        <AddBtn label="Texte" onClick={() => addText("text")} />
        <AddBtn label="Indice" onClick={() => addText("hint")} accent="var(--orange)" />
        <AddBtn label="Image / Fichier" icon="upload" onClick={() => fileRef.current?.click()} />
        <input
          ref={fileRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) addMedia(f);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

function AddBtn({ label, onClick, icon, accent }: { label: string; onClick: () => void; icon?: IconName; accent?: string }) {
  return (
    <button style={{ ...styles.addBtn, ...(accent ? { color: accent, borderColor: accent } : {}) }} onClick={onClick} type="button">
      {icon ? <Icon name={icon} size={15} /> : <span style={styles.plus}>+</span>}
      {label}
    </button>
  );
}

function SortableBlock({
  block,
  onPatchLocal,
  onPersist,
  onRemove,
}: {
  block: Block;
  onPatchLocal: (id: number, patch: Partial<Block>) => void;
  onPersist: (id: number, data: { text?: string; caption?: string; alt?: string }) => Promise<unknown>;
  onRemove: (id: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const isHint = block.kind === "hint";
  const accent = isHint ? "var(--orange)" : "var(--blue)";
  const label =
    block.kind === "text" ? "Texte"
    : block.kind === "hint" ? "Indice"
    : block.kind === "image" ? "Image"
    : "Fichier";

  return (
    <div ref={setNodeRef} style={{ ...styles.block, borderLeft: `3px solid ${accent}`, ...style }}>
      <div style={styles.blockHead}>
        <button style={styles.handle} {...attributes} {...listeners} aria-label="Déplacer le bloc" type="button">
          <DragDots />
        </button>
        <span style={{ ...styles.kindLabel, color: accent }}>{label}</span>
        <span style={{ flex: 1 }} />
        <button style={styles.del} onClick={() => onRemove(block.id)} aria-label="Supprimer le bloc" type="button">
          <Icon name="trash" size={14} />
        </button>
      </div>

      {(block.kind === "text" || block.kind === "hint") && (
        <textarea
          style={styles.textarea}
          value={block.text ?? ""}
          placeholder={isHint ? "Contenu de l'indice (Markdown)" : "Texte de la consigne (Markdown)"}
          onChange={(e) => onPatchLocal(block.id, { text: e.target.value })}
          onBlur={(e) => onPersist(block.id, { text: e.target.value })}
        />
      )}

      {block.kind === "image" && block.attachment && (
        <div style={styles.mediaBody}>
          <img
            src={`/api/exercises/attachments/${block.attachment.id}`}
            alt={block.alt ?? ""}
            style={styles.preview}
          />
          <input
            style={styles.input}
            value={block.caption ?? ""}
            placeholder="Légende (affichée sous l'image)"
            onChange={(e) => onPatchLocal(block.id, { caption: e.target.value })}
            onBlur={(e) => onPersist(block.id, { caption: e.target.value })}
          />
          <input
            style={styles.input}
            value={block.alt ?? ""}
            placeholder="Texte alternatif (accessibilité)"
            onChange={(e) => onPatchLocal(block.id, { alt: e.target.value })}
            onBlur={(e) => onPersist(block.id, { alt: e.target.value })}
          />
        </div>
      )}

      {block.kind === "file" && block.attachment && (
        <div style={styles.mediaBody}>
          <span style={styles.fileName}>
            <Icon name="file" size={14} /> {block.attachment.filename}
          </span>
          <input
            style={styles.input}
            value={block.caption ?? ""}
            placeholder="Libellé du lien"
            onChange={(e) => onPatchLocal(block.id, { caption: e.target.value })}
            onBlur={(e) => onPersist(block.id, { caption: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

function DragDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {[6, 12, 18].flatMap((y) => [8, 16].map((x) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />
      )))}
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: 10 },
  empty: { color: "var(--text-faint)", fontSize: 13, fontStyle: "italic", margin: 0 },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  block: {
    background: "var(--bg-panel)",
    borderRadius: "var(--radius)",
    padding: "8px 10px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  blockHead: { display: "flex", alignItems: "center", gap: 8 },
  handle: {
    background: "transparent",
    border: "none",
    color: "var(--text-faint)",
    cursor: "grab",
    padding: 2,
    display: "inline-flex",
    touchAction: "none",
  },
  kindLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 },
  del: {
    background: "transparent",
    border: "none",
    color: "var(--text-muted)",
    cursor: "pointer",
    display: "inline-flex",
    padding: 2,
  },
  textarea: {
    width: "100%",
    minHeight: 70,
    resize: "vertical",
    background: "var(--bg-base)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    padding: "8px 10px",
    color: "var(--text)",
    fontSize: 13,
    fontFamily: "var(--font-mono)",
    lineHeight: 1.5,
  },
  mediaBody: { display: "flex", flexDirection: "column", gap: 6 },
  preview: {
    maxWidth: "100%",
    maxHeight: 220,
    objectFit: "contain",
    alignSelf: "flex-start",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border-soft)",
  },
  fileName: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "var(--text)",
    fontSize: 13,
  },
  input: {
    width: "100%",
    background: "var(--bg-base)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    padding: "6px 9px",
    color: "var(--text)",
    fontSize: 13,
    fontFamily: "inherit",
  },
  addBar: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 2 },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "var(--bg-panel)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    color: "var(--text)",
    padding: "9px 16px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  plus: { fontSize: 16, fontWeight: 700, lineHeight: 1 },
};
